import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Chrome } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { FadeUp, Overline, MaskedLine } from "../components/Reveal";

const Auth = () => {
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = searchParams.get("next") || "/account";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success("Welcome back to the maison");
      } else {
        await register(name, email, password);
        toast.success("Welcome to Vincen");
      }
      navigate(next);
    } catch (err) {
      toast.error(formatApiError(err, "Authentication failed"));
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const inputCls =
    "w-full bg-[#141414] border border-white/10 px-5 py-4 text-sm text-[#F5F5F5] placeholder:text-zinc-600 focus:border-gold focus:outline-none transition-colors duration-300";

  return (
    <div data-testid="auth-page" className="min-h-screen pt-32 md:pt-40 pb-28 px-6">
      <div className="max-w-md mx-auto">
        <Overline className="text-center">Members</Overline>
        <h1 className="mt-4 text-center font-serif font-light text-[#F5F5F5]">
          <MaskedLine delay={0.1} className="text-4xl md:text-5xl">
            {mode === "login" ? "Return, Conqueror" : "Join the Maison"}
          </MaskedLine>
        </h1>

        <FadeUp delay={0.2} className="mt-12">
          <div className="flex border border-white/10 mb-8">
            {["login", "register"].map((m) => (
              <button
                key={m}
                data-testid={`auth-mode-${m}`}
                onClick={() => setMode(m)}
                className={`flex-1 py-3 text-xs tracking-[0.3em] uppercase transition-colors duration-300 ${
                  mode === m ? "bg-gold text-black font-semibold" : "text-zinc-500 hover:text-zinc-200"
                }`}
              >
                {m === "login" ? "Sign In" : "Register"}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="flex flex-col gap-4">
            {mode === "register" && (
              <input
                data-testid="auth-name-input"
                type="text"
                placeholder="Full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className={inputCls}
              />
            )}
            <input
              data-testid="auth-email-input"
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className={inputCls}
            />
            <input
              data-testid="auth-password-input"
              type="password"
              placeholder="Password (min 6 characters)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              className={inputCls}
            />
            <button
              data-testid="auth-submit-button"
              type="submit"
              disabled={loading}
              className="mt-4 py-4 bg-gold text-black text-xs tracking-[0.35em] uppercase font-semibold hover:bg-gold-hover transition-colors duration-500 disabled:opacity-50"
            >
              {loading ? "..." : mode === "login" ? "Sign In" : "Create Account"}
            </button>
          </form>

          <div className="mt-8 flex items-center gap-4">
            <span className="flex-1 h-px bg-white/10" />
            <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-600">or</span>
            <span className="flex-1 h-px bg-white/10" />
          </div>
          <button
            data-testid="google-signin-button"
            onClick={googleLogin}
            className="mt-8 w-full py-4 border border-white/20 text-zinc-300 text-xs tracking-[0.35em] uppercase hover:border-gold hover:text-gold transition-colors duration-500 flex items-center justify-center gap-3"
          >
            <Chrome size={14} /> Continue with Google
          </button>
        </FadeUp>
      </div>
    </div>
  );
};

export default Auth;
